import { getEnvByHostname } from '../utils/env';
import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Select, Button, Space, message, Row, Col, Divider, Tag, Tooltip } from 'antd';
import { UserAddOutlined, ReloadOutlined, GlobalOutlined, BankOutlined, SwapOutlined, WalletOutlined, ApiOutlined, LinkOutlined } from '@ant-design/icons';
import ReactJson from '@microlink/react-json-view';
import { getChannelLogo, buildChannelOptions } from '../utils/channelLogos';
import { testApi } from '../services/api';
import { testTemplates, buildRequestBody } from '../utils/testTemplates';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageCache } from '../contexts/PageCacheContext';

const PAYMENT_METHOD_CONFIG = {
  VIRTUAL_ACCOUNT:  { icon: <GlobalOutlined />,  color: 'blue',    label: 'Virtual Account' },
  PIX:              { icon: <SwapOutlined />,    color: 'purple',  label: 'PIX' },
  VIETQR:           { icon: <BankOutlined />,    color: 'green',   label: 'VietQR' },
  ZEN_WALLET:       { icon: <WalletOutlined />,  color: 'gold',    label: 'Zen Wallet' },
  INTERACE_REQUEST: { icon: <ApiOutlined />,     color: 'orange',  label: 'Interac' },
  LEAN:             { icon: <LinkOutlined />,    color: 'cyan',    label: 'LEAN' },
};

// MDAQ 各币种支持的网络类型（network）
const MDAQ_CURRENCY_NETWORKS = {
  SGD: ['local', 'swift'],
  HKD: ['local', 'swift'],
  IDR: ['local'],
  USD: ['wire_routing_number', 'ach_routing_number', 'swift'],
  VND: ['local'],
  PHP: ['local'],
  MYR: ['local'],
};

const paymentMethodOption = (value) => {
  const cfg = PAYMENT_METHOD_CONFIG[value] || {};
  return {
    value,
    label: (
      <Space size={6}>
        <Tag icon={cfg.icon} color={cfg.color} style={{ margin: 0, fontSize: 12 }}>{cfg.label || value}</Tag>
      </Space>
    ),
  };
};

export default function AccountCreate() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [userChannels, setUserChannels] = useState([]);
  const [formUserType, setFormUserType] = useState(null);
  const { t } = useLanguage();
  const { cache, save: saveCache } = usePageCache('accountCreate');

  useEffect(() => {
    loadUserChannels();
    if (cache) {
      if (cache.result) setResult(cache.result);
      if (cache.selectedChannel) setSelectedChannel(cache.selectedChannel);
      if (cache.formValues) form.setFieldsValue(cache.formValues);
    } else {
      const savedData = localStorage.getItem('accountCreateFormData');
      if (savedData) {
        try {
          const formData = JSON.parse(savedData);
          form.setFieldsValue(formData);
          if (formData.channelCode) setSelectedChannel(formData.channelCode);
        } catch (e) {}
      }
    }
  }, [form]);

  useEffect(() => {
    return () => {
      saveCache({ result, selectedChannel, formValues: form.getFieldsValue() });
    };
  });

  const loadUserChannels = async () => {
    try {
      const { data } = await testApi.getChannelCurrencies();
      const responseData = data.data;
      const channelCurrencyMap = responseData?.responseBody || responseData || {};
      const channels = Object.keys(channelCurrencyMap).map(channelCode => ({
        channelCode,
        currencies: [...new Set(channelCurrencyMap[channelCode] || [])]
      }));
      
      setUserChannels(channels);
      if (channels.length > 0) {
        const firstChannel = channels[0];
        form.setFieldsValue({ 
          channelCode: firstChannel.channelCode,
          currency: firstChannel.currencies[0]
        });
        setSelectedChannel(firstChannel.channelCode);
      }
    } catch (error) {
      console.error('Failed to load user channels:', error);
      setUserChannels([]);
    }
  };


  const saveFormData = () => {
    const values = form.getFieldsValue();
    localStorage.setItem('accountCreateFormData', JSON.stringify(values));
  };

  const handleValuesChange = (changedValues, allValues) => {
    if ('userType' in changedValues) {
      setFormUserType(changedValues.userType);
    }
    // MDAQ：币种切换后 network 选项随之变化，清空已选 network 避免残留非法值
    if ('currency' in changedValues && selectedChannel === 'MDAQ') {
      form.setFieldValue('network', undefined);
    }
    saveFormData();
  };

  const handleChannelChange = (channelCode) => {
    setSelectedChannel(channelCode);
    // MDAQ：币种用前端支持列表（后端可能只返回部分），默认 SGD
    if (channelCode === 'MDAQ') {
      form.setFieldsValue({ currency: 'SGD', network: undefined });
      saveFormData();
      return;
    }
    const channel = userChannels.find(c => c.channelCode === channelCode);
    if (channel && channel.currencies.length > 0) {
      form.setFieldsValue({ currency: channel.currencies[0] });
    }
    saveFormData();
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const requestBody = buildRequestBody('ACCOUNT_CREATE', values, values.channelCode, values.currency);
      
      const username = localStorage.getItem('username');
      const response = await testApi.executeTest({
        testType: 'ACCOUNT_CREATE',
        channelCode: values.channelCode,
        businessPartner: values.businessPartner || 'DEMO_ACCOUNT',
        currency: values.currency,
        env: values.env || getEnvByHostname(),
        executor: username,
        requestBody: JSON.stringify(requestBody)
      });

      const result = response.data;
      if (result.data && result.data.responseBody && typeof result.data.responseBody === 'string') {
        try {
          result.data.responseBody = JSON.parse(result.data.responseBody);
        } catch (e) {
          console.warn('Failed to parse responseBody as JSON:', e);
        }
      }
      
      setResult(result);
      
      // 优化提示信息，优先使用 responseBody 中的 msg
      let successMsg = t('accountCreateSuccess');
      let errorMsg = result.msg || t('failed');
      
      if (result.data && result.data.responseBody && result.data.responseBody.msg) {
        if (result.code === '200') {
          successMsg = result.data.responseBody.msg;
        } else {
          errorMsg = result.data.responseBody.msg;
        }
      }
      
      if (result.code === '200') {
        message.success(successMsg);
      } else {
        message.warning(`${t('accountCreateFailed')}: ${errorMsg}`);
      }
    } catch (error) {
      console.error('Account create error:', error);
      message.error(t('accountCreateFailed') + ': ' + (error.response?.data?.msg || error.message));
      setResult(error.response?.data || { error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setResult(null);
    setSelectedChannel(null);
  };

  const renderFormFields = () => {
    if (!selectedChannel) {
      return (
        <div style={{ 
          padding: 60, 
          textAlign: 'center', 
          color: '#6b7280',
          background: '#f9fafb',
          borderRadius: 8,
          border: '2px dashed #d1d5db'
        }}>
          <UserAddOutlined style={{ fontSize: 48, marginBottom: 16, color: '#d1d5db' }} />
          <div>{t('selectChannelFirst')}</div>
        </div>
      );
    }

    const template = testTemplates.ACCOUNT_CREATE;
    const fields = template.channels[selectedChannel];
    
    if (!fields || fields.length === 0) {
      return <div>{t('noChannelConfig')}</div>;
    }

    const currentUserType = form.getFieldValue('userType');

    return (
      <Row gutter={[16, 16]}>
        {fields.map(field => {
          // showWhen 条件控制：字段值不匹配时隐藏
          if (field.showWhen) {
            const watchedValue = field.showWhen.field === 'userType' ? (formUserType ?? currentUserType) : form.getFieldValue(field.showWhen.field);
            if (watchedValue !== field.showWhen.value) return null;
          }
          return (
          <Col xs={24} sm={12} key={field.name}>
            <Form.Item 
              label={
                <Space size={4}>
                  <span>{t(field.name) || field.label}</span>
                  {field.tip && (
                    <Tooltip title={t(field.tip)}>
                      <span style={{ cursor: 'help', color: '#1890ff', fontSize: 12 }}>?</span>
                    </Tooltip>
                  )}
                </Space>
              }
              name={field.name}
              rules={[{ required: field.required, message: `${field.type === 'select' ? t('pleaseSelect', t(field.name) || field.label) : t('pleaseInput', t(field.name) || field.label)}` }]}
              initialValue={field.default}
            >
              {field.type === 'select' ? (
                <Select
                  placeholder={field.placeholder || t('pleaseSelect', t(field.name) || field.label)}
                  options={
                    (selectedChannel === 'MDAQ' && field.name === 'network')
                      ? (MDAQ_CURRENCY_NETWORKS[form.getFieldValue('currency')] || []).map(o => ({ label: o, value: o }))
                      : field.options.map(o => field.name === 'paymentMethod' ? paymentMethodOption(o) : ({ label: o, value: o }))
                  }
                />
              ) : (
                <Input placeholder={field.placeholder || t('pleaseInput', t(field.name) || field.label)} autoComplete="new-password" type="text" />
              )}
            </Form.Item>
          </Col>
        )})}
      </Row>
    );
  };

  return (
    <div style={{ padding: '16px 24px', height: 'calc(100vh - 64px)', overflow: 'auto' }}>
      <div className="fade-in-up">
        <div style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '12px 24px',
          borderRadius: '8px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
        }}>
          <Space size={12}>
            <UserAddOutlined style={{ color: '#fff', fontSize: 20 }} />
            <span style={{ fontSize: 18, fontWeight: 600, color: '#fff' }}>{t('accountCreate')}</span>
          </Space>
          <Button icon={<ReloadOutlined />} onClick={handleReset} type="text" style={{ color: '#fff' }}>
            {t('reset')}
          </Button>
        </div>

        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Card className="modern-card" bodyStyle={{ padding: '24px' }}>
              <Form form={form} layout="vertical" onFinish={handleSubmit} onValuesChange={handleValuesChange} autoComplete="off">
                <Form.Item name="businessPartner" hidden>
                  <Input />
                </Form.Item>
                
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: '#374151' }}>
                  ⚙️ {t('basicConfig')}
                </div>

                <Form.Item label={t('channel')} name="channelCode" rules={[{ required: true }]}>
                  <Select 
                    placeholder={t('selectChannel')}
                    onChange={handleChannelChange}
                    options={buildChannelOptions(userChannels.map(c => c.channelCode))}
                  />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item label={t('currency')} name="currency" rules={[{ required: true }]} initialValue="USD">
                      <Select options={
                        selectedChannel === 'MDAQ'
                          ? Object.keys(MDAQ_CURRENCY_NETWORKS).map(c => ({ label: c, value: c }))
                          : (userChannels.find(c => c.channelCode === form.getFieldValue('channelCode'))?.currencies || []).map(c => ({ label: c, value: c }))
                      } />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider style={{ margin: '16px 0' }} />
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: '#374151' }}>
                  📋 {t('accountCreateParams')}
                </div>

                {renderFormFields()}

                <Button 
                  type="primary" 
                  htmlType="submit" 
                  icon={<UserAddOutlined />} 
                  loading={loading} 
                  size="large" 
                  block
                  className="primary-button"
                  style={{ height: 48, marginTop: 16 }}
                >
                  {loading ? t('creatingAccount') : t('createAccount')}
                </Button>
              </Form>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card className="modern-card" title={`📋 ${t('accountCreateResult')}`} bodyStyle={{ padding: '24px' }}>
              {result ? (
                <div className="json-viewer">
                  <ReactJson 
                    src={result} 
                    collapsed={false}
                    theme="rjv-default"
                    displayDataTypes={false}
                    displayObjectSize={false}
                    enableClipboard={true}
                  />
                </div>
              ) : (
                <div style={{ 
                  padding: 60, 
                  textAlign: 'center', 
                  color: '#999',
                  background: '#f9fafb',
                  borderRadius: 8,
                  border: '2px dashed #d1d5db'
                }}>
                  <UserAddOutlined style={{ fontSize: 48, marginBottom: 16, color: '#d1d5db' }} />
                  <div>{t('submitToShowResult')}</div>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}
